import type { DochatAuthProvider, DochatAuthPublicReason } from "@do/shared-types";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { createAuthTransaction, waitForSignIn } from "../gateways/doChatApi";
import { openAuthPopup } from "./handshake";
import { TX_TTL_MS } from "./ttl";

// After this long without a result, the login screen suggests another cause than the popup.
export const DIFFERENT_USER_HINT_DELAY_MS = 60_000;

export type SignInState =
  | { step: "ready" }
  | { step: "waiting"; showDifferentUserHint: boolean }
  | { step: "popup_blocked" }
  | { step: "failed"; reason: DochatAuthPublicReason; reference: string | null };

type Transaction = { txId: string; txSecret: string };

// A prepared transaction is replaced this long before it expires on the server.
export const TX_RENEWAL_MARGIN_MS = 60_000;

/**
 * One provider's sign-in, from the login screen: its transaction exists before the click, so
 * that the popup opens synchronously in the click handler; then the chat long-polls the server
 * for the outcome, which the popup cannot hand back itself.
 *
 * The next transaction is always ready: it is renewed before it expires, and prepared as soon as
 * a popup opens, so that a click while waiting (the user closed the popup, or wants to start
 * over) starts a new attempt in the same popup window and drops the previous one.
 */
export function useSignIn({
  dochatConfigId,
  provider,
  locale,
  authUrl,
  onTicket,
}: {
  dochatConfigId: string;
  provider: DochatAuthProvider;
  locale: string;
  authUrl: string | null;
  onTicket: (ticket: string) => void;
}) {
  const [state, setState] = useState<SignInState>({ step: "ready" });
  const transaction = useRef<Transaction | null>(null);
  const renewal = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // The current attempt: an older one's answers are ignored.
  const attempt = useRef(0);
  const disposed = useRef(false);

  const prepare = useCallback(async () => {
    clearTimeout(renewal.current);
    try {
      const tx = await createAuthTransaction({ dochatConfigId, provider, locale });
      if (disposed.current) return;
      transaction.current = tx;
      renewal.current = setTimeout(() => void prepare(), TX_TTL_MS - TX_RENEWAL_MARGIN_MS);
    } catch (error: unknown) {
      if (disposed.current) return;
      const code = axios.isAxiosError(error) && error.response?.status === 429;
      setState({ step: "failed", reason: code ? "rate_limited" : "unavailable", reference: null });
    }
  }, [dochatConfigId, provider, locale]);

  useEffect(() => {
    disposed.current = false;
    void prepare();
    return () => {
      disposed.current = true;
      clearTimeout(renewal.current);
    };
  }, [prepare]);

  const poll = useCallback(
    async (tx: Transaction, current: number) => {
      const isCurrent = () => !disposed.current && attempt.current === current;
      const hint = setTimeout(() => {
        if (isCurrent()) setState({ step: "waiting", showDifferentUserHint: true });
      }, DIFFERENT_USER_HINT_DELAY_MS);
      try {
        while (isCurrent()) {
          const answer = await waitForSignIn(tx);
          if (!isCurrent()) return;
          if (answer.status === "pending") continue;
          if (answer.status === "completed") {
            onTicket(answer.ticket);
            return;
          }
          setState({
            step: "failed",
            reason: answer.status === "failed" ? answer.reason : "expired",
            reference: tx.txId.slice(0, 8),
          });
          return;
        }
      } catch (error: unknown) {
        if (!isCurrent()) return;
        const rateLimited = axios.isAxiosError(error) && error.response?.status === 429;
        setState({
          step: "failed",
          reason: rateLimited ? "rate_limited" : "sign_in_failed",
          reference: tx.txId.slice(0, 8),
        });
      } finally {
        clearTimeout(hint);
      }
    },
    [onTicket]
  );

  /** To call from the click handler itself, so that popup blockers let the window open. */
  const open = useCallback(() => {
    const tx = transaction.current;
    if (!tx || !authUrl) {
      // While waiting, the next transaction may still be on its way: the attempt goes on.
      setState((current) =>
        current.step === "waiting" && authUrl
          ? current
          : { step: "failed", reason: "unavailable", reference: null }
      );
      return;
    }
    const { popup } = openAuthPopup({ authUrl, txId: tx.txId, txSecret: tx.txSecret });
    if (!popup) {
      setState({ step: "popup_blocked" });
      return;
    }
    // A transaction is single-use: the next attempt gets a new one, prepared right away.
    transaction.current = null;
    void prepare();
    attempt.current += 1;
    setState({ step: "waiting", showDifferentUserHint: false });
    void poll(tx, attempt.current);
  }, [authUrl, poll, prepare]);

  return { state, open };
}
